<!--
  @component Toolbar
  @description Top toolbar with search, actions, and navigation
-->
<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { t } from '@/i18n';
  import { selectedLibrary, isAuthenticated } from '@stores';
  import { logout } from '@stores/authStore';
  import { api } from '@/config';

  // Props for responsive state
  export let isMobile = false;
  export let isTablet = false;

  const dispatch = createEventDispatcher<{
    search: { query: string };
    newItem: void;
    openImport: void;
    openScan: void;
    showSettings: void;
    toggleCollections: void;
  }>();

  // State
  let searchInput = '';
  let searchTimeout: ReturnType<typeof setTimeout>;

  // Debounced search
  function handleSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    searchInput = target.value;

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      dispatch('search', { query: searchInput });
    }, 300);
  }

  function handleNewItem() {
    dispatch('newItem');
  }

  function handleOpenImport() {
    dispatch('openImport');
  }

  function handleOpenScan() {
    dispatch('openScan');
  }

  function handleShowSettings() {
    dispatch('showSettings');
  }

  function handleLogout() {
    logout();
  }

  function handleToggleCollections() {
    dispatch('toggleCollections');
  }
</script>

<div class="toolbar" class:mobile={isMobile} class:tablet={isTablet}>
  <!-- Hamburger Menu -->
  <div class="hamburger-container">
    <input type="checkbox" id="hamburger-toggle" class="hamburger-checkbox" />
    <label for="hamburger-toggle" class="hamburger-icon" aria-label={$t('a11y.openMenu')}>
      ☰
    </label>
    <div class="hamburger-menu">
      <button class="hamburger-menu-item" on:click={handleShowSettings}>
        <span>⚙️</span>
        <span>{$t('common.settings')}</span>
      </button>
      <button class="hamburger-menu-item" on:click={handleLogout}>
        <span>🚪</span>
        <span>{$t('common.logout')}</span>
      </button>
    </div>
  </div>

  <!-- Collections Toggle (tablet/mobile) -->
  {#if isTablet}
    <button
      class="toolbar-button collections-toggle"
      on:click={handleToggleCollections}
      aria-label={$t('collection.collections')}
    >
      📚 {$t('collection.collections')}
    </button>
  {/if}

  <!-- Search -->
  <div class="toolbar-search">
    <input type="checkbox" id="search-toggle" class="search-checkbox" />
    <label for="search-toggle" class="search-icon" aria-label={$t('common.search')}> 🔍 </label>
    <div class="search-dropdown">
      <input
        class="search-input"
        placeholder={$t('search.placeholder')}
        value={searchInput}
        on:input={handleSearchInput}
      />
    </div>
  </div>

  <!-- New Item Button -->
  <button class="toolbar-button primary" on:click={handleNewItem}>
    {#if isMobile}
      ➕
    {:else}
      {$t('item.newItem')}
    {/if}
  </button>

  <!-- Import Button -->
  <button class="toolbar-button import-btn" on:click={handleOpenImport} title={$t('import.title')}>
    {#if isMobile}
      📥
    {:else}
      {$t('import.importButton')}
    {/if}
  </button>

  <!-- Scan Button -->
  <button class="toolbar-button scan-btn" on:click={handleOpenScan} title={$t('scan.title') || 'Scan ISBN'}>
    {#if isMobile}
      📷
    {:else}
      {$t('scan.scanButton') || 'Scan'}
    {/if}
  </button>

  <!-- App Title -->
  <div class="toolbar-center">
    <span class="toolbar-title">{$t('common.appName')}</span>
  </div>

  <!-- Portal Logo -->
  <div class="toolbar-right">
    <a href={api.proxy.portalUrl} class="portal-link" title="Retour au portail Spectral Cloud">
      <img src="/logo.png" alt="Spectral Cloud" class="header-logo" />
    </a>
  </div>
</div>

<style>
  .toolbar {
    background: var(--color-bg-secondary, #3c3c3c);
    padding: 8px 12px;
    border-bottom: 1px solid var(--color-border, #555);
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 40px;
    position: relative;
  }

  .toolbar-button {
    background: var(--color-bg-tertiary, #4a4a4a);
    border: 1px solid var(--color-border, #666);
    color: var(--color-text-primary, #e8e8e8);
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
  }

  .toolbar-button:hover {
    background: var(--color-bg-hover, #555);
  }

  .toolbar-button.primary {
    background: var(--color-accent, #5c7cfa);
    border-color: var(--color-accent, #5c7cfa);
  }

  .toolbar-button.primary:hover {
    background: var(--color-accent-hover, #4c6ef5);
  }

  .hamburger-container {
    position: relative;
    display: inline-block;
    margin-right: 16px;
  }

  .hamburger-checkbox {
    display: none;
  }

  .hamburger-icon {
    color: var(--color-text-primary, #e8e8e8);
    cursor: pointer;
    font-size: 29px;
    user-select: none;
    padding: 4px;
    line-height: 1;
    transition: color 0.2s;
  }

  .hamburger-icon:hover {
    color: var(--color-accent, #5c7cfa);
  }

  .hamburger-checkbox:checked ~ .hamburger-icon {
    color: var(--color-accent, #5c7cfa);
  }

  .hamburger-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background: var(--color-bg-secondary, #383838);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 1000;
    min-width: 150px;
    margin-top: 4px;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s,
      visibility 0.2s;
  }

  .hamburger-checkbox:checked ~ .hamburger-menu {
    opacity: 1;
    visibility: visible;
  }

  .hamburger-menu-item {
    width: 100%;
    padding: 10px 12px;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;
    background: none;
    color: var(--color-text-primary, #e8e8e8);
    border-bottom: 1px solid var(--color-border-light, #444);
    transition: background-color 0.2s;
    text-align: left;
  }

  .hamburger-menu-item:hover {
    background: var(--color-bg-hover, #444);
  }

  .hamburger-menu-item:last-child {
    border-bottom: none;
  }

  .toolbar-center {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  .toolbar-title {
    font-size: 18px;
    color: var(--color-text-primary, #e8e8e8);
    font-weight: 600;
  }

  .toolbar-right {
    margin-left: auto;
  }

  .portal-link {
    display: flex;
    align-items: center;
    opacity: 0.7;
    transition: opacity 0.2s;
  }

  .portal-link:hover {
    opacity: 1;
  }

  .header-logo {
    height: 24px;
    width: auto;
  }

  .toolbar-search {
    position: relative;
    margin-left: 8px;
  }

  .search-checkbox {
    display: none;
  }

  .search-icon {
    color: var(--color-text-primary, #e8e8e8);
    cursor: pointer;
    font-size: 20px;
    user-select: none;
    padding: 4px;
    transition: color 0.2s;
  }

  .search-icon:hover {
    color: var(--color-accent, #5c7cfa);
  }

  .search-checkbox:checked ~ .search-icon {
    color: var(--color-accent, #5c7cfa);
  }

  .search-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 300px;
    padding: 8px;
    background: var(--color-bg-secondary, #383838);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 1000;
    margin-top: 4px;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s,
      visibility 0.2s;
  }

  .search-checkbox:checked ~ .search-dropdown {
    opacity: 1;
    visibility: visible;
  }

  .search-input {
    width: 100%;
    padding: 8px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 13px;
    box-sizing: border-box;
  }

  .search-input:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .search-input::placeholder {
    color: var(--color-text-muted, #888);
  }

  /* Collections toggle button */
  .collections-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  /* ==========================================================================
     Responsive Styles
     ========================================================================== */

  /* Tablet */
  .toolbar.tablet {
    padding: 8px;
    gap: 8px;
  }

  .toolbar.tablet .toolbar-title {
    font-size: 16px;
  }

  /* Mobile */
  .toolbar.mobile {
    padding: 8px 12px;
    gap: 8px;
    min-height: 56px;
  }

  .toolbar.mobile .hamburger-container {
    margin-right: 8px;
  }

  .toolbar.mobile .toolbar-center {
    position: static;
    transform: none;
    flex: 1;
    text-align: center;
  }

  .toolbar.mobile .toolbar-title {
    font-size: 16px;
  }

  .toolbar.mobile .toolbar-right {
    display: none;
  }

  .toolbar.mobile .toolbar-button.primary {
    padding: 8px 12px;
    font-size: 16px;
  }

  .toolbar.mobile .search-dropdown {
    left: auto;
    right: 0;
    min-width: calc(100vw - 24px);
    max-width: 400px;
  }

  /* Touch-friendly targets */
  @media (pointer: coarse) {
    .hamburger-icon,
    .search-icon {
      padding: 8px;
    }

    .toolbar-button {
      min-height: 44px;
      min-width: 44px;
    }

    .hamburger-menu-item {
      min-height: 48px;
    }
  }
</style>
